/**
 * BusinessSwitcher — header dropdown for switching active business.
 *
 * Only rendered for tenant_owner / super_admin. Single-business users
 * don't see this — they have one business and can't switch.
 *
 * UX intent: lightweight, always-visible reminder of "which business
 * am I in right now." Click to see the list, with the active one
 * marked. A "Back to portfolio" item at the bottom lets them return
 * to the overview.
 *
 * Phase 0.4b will add: per-business indicators (alerts, low stock),
 * favorites/pinning, keyboard shortcut to open (cmd+B).
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Building2, Check, LayoutGrid, ChevronDown, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useAuthStore from "@/stores/authStore";
import useBusinessStore from "@/stores/businessStore";
import { useMyBusinesses } from "@/hooks/useBusinesses";

export const BusinessSwitcher: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const activeBusinessSlug = useBusinessStore((s) => s.activeBusinessSlug);
  const setActiveBusinessSlug = useBusinessStore(
    (s) => s.setActiveBusinessSlug,
  );

  const { data: businesses = [], isLoading } = useMyBusinesses();

  // Don't render for non-tenant-wide roles — they have one business.
  if (user?.role !== "tenant_owner" && user?.role !== "super_admin") {
    return null;
  }

  // Single-business tenant_owners don't need the switcher either.
  // (Their portfolio-vs-direct routing already handled.)
  if (!isLoading && businesses.length <= 1) {
    return null;
  }

  const activeBusiness = businesses.find((b) => b.slug === activeBusinessSlug);
  const activeBusinessName = activeBusiness?.name || (activeBusinessSlug ? "Select business" : "All businesses");

  // All businesses: Money, Insights, People and Admin keep working (each
  // page shows its own business bar); selling and stock open a business.
  const handleAll = () => {
    setActiveBusinessSlug(null);
    void queryClient.invalidateQueries();
    navigate("/group");
  };

  const handleSwitch = (slug: string) => {
    if (slug === activeBusinessSlug) return;
    setActiveBusinessSlug(slug);
    // Same page, the other business's data: drop what was loaded for the
    // last one so nothing from it shows (it used to stay until stale).
    void queryClient.invalidateQueries();
  };

  const handleBackToPortfolio = () => {
    navigate("/portfolio");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-9 min-w-0 gap-2 px-2 hover:bg-muted sm:px-3"
        >
          <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 max-w-[110px] truncate text-sm font-medium text-foreground sm:max-w-[160px] xl:max-w-[220px]">
            {activeBusinessName}
          </span>
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuItem onClick={handleAll} className="flex items-center justify-between gap-2 py-2">
          <div className="flex items-center gap-2 min-w-0">
            <Layers className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="text-sm font-medium">All businesses</span>
          </div>
          {!activeBusinessSlug && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Open one business
        </DropdownMenuLabel>

        {businesses.map((business) => {
          const isActive = business.slug === activeBusinessSlug;
          return (
            <DropdownMenuItem
              key={business._id}
              onClick={() => handleSwitch(business.slug)}
              className="flex items-center justify-between gap-2 py-2"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-sm truncate">{business.name}</span>
                {business.isDefault && (
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    (Primary)
                  </span>
                )}
              </div>
              {isActive && (
                <Check className="h-3.5 w-3.5 text-primary shrink-0" />
              )}
            </DropdownMenuItem>
          );
        })}

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleBackToPortfolio} className="py-2">
          <LayoutGrid className="mr-2 h-3.5 w-3.5" />
          <span className="text-sm">Back to portfolio</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
